// Design System SortMenu Component
// Unified menu component for sorting items

import React, { memo } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  useTheme,
  styled,
  alpha,
} from '@mui/material';
import {
  Check as CheckIcon,
  Sort as SortIcon,
  ArrowUpward as AscendingIcon,
  ArrowDownward as DescendingIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { borderTokens, shadowTokens, spacingTokens, transitionTokens } from '../../tokens';

// Styled components
const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: borderTokens.lg,
    boxShadow: shadowTokens.medium,
    minWidth: 220,
    marginTop: spacingTokens.component.xs,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  margin: `${spacingTokens.component.xs} ${spacingTokens.component.sm}`,
  borderRadius: borderTokens.md,
  transition: transitionTokens.smooth,
  
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'translateX(2px)',
  },
  
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    fontWeight: 500,
    
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
    },
  },
}));

const SortHeader = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.875rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: theme.palette.text.secondary,
}));

/**
 * SortMenu - Unified menu component for sorting
 * 
 * Features:
 * - Sort by field selection
 * - Sort direction toggle
 * - Customizable sort options
 * - Visual indicators for active sort
 * - Smooth transitions
 * 
 * @example
 * // Basic usage
 * <SortMenu
 *   anchorEl={anchorEl}
 *   open={open}
 *   onClose={handleClose}
 *   sortField="name"
 *   sortDirection="asc"
 *   onSortChange={handleSortChange}
 *   options={[
 *     { field: 'name', label: 'Name' },
 *     { field: 'date', label: 'Date' },
 *     { field: 'priority', label: 'Priority' }
 *   ]}
 * />
 * 
 * // With custom direction change handler
 * <SortMenu
 *   anchorEl={anchorEl}
 *   open={open}
 *   onClose={handleClose}
 *   sortField={sort.field}
 *   sortDirection={sort.direction}
 *   onSortChange={(field, direction) => setSort({ field, direction })}
 *   onDirectionChange={(newDirection) => setSort({ ...sort, direction: newDirection })}
 *   options={sortOptions}
 * />
 */
const SortMenu = ({
  anchorEl,
  open,
  onClose,
  
  // Sort state
  sortField = '',
  sortDirection = 'asc',
  
  // Handlers
  onSortChange,
  onDirectionChange,
  
  // Options
  options = [],
  defaultOptions = true,
  
  // Display
  showDirection = true,
  showHeader = true,
  
  // Style
  width = 220,
  
  ...props
}) => {
  const theme = useTheme();
  
  // Default sort options
  const defaultSortOptions = [
    { field: 'name', label: 'Name' },
    { field: 'createdAt', label: 'Created Date' },
    { field: 'updatedAt', label: 'Last Modified' },
    { field: 'priority', label: 'Priority' },
    { field: 'status', label: 'Status' },
  ];
  
  // Use provided options or defaults
  const sortOptions = options.length > 0 ? options : (defaultOptions ? defaultSortOptions : []);
  
  // Handle sort field selection
  const handleSortClick = (field) => {
    if (field === sortField && onDirectionChange) {
      // If clicking the same field, toggle direction
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      onDirectionChange(newDirection);
      if (onSortChange) {
        onSortChange(field, newDirection);
      }
    } else {
      // If clicking a different field, set it with default direction
      if (onSortChange) {
        onSortChange(field, 'asc');
      }
    }
    onClose();
  };
  
  // Handle explicit direction change
  const handleDirectionToggle = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    if (onDirectionChange) {
      onDirectionChange(newDirection);
    }
    if (onSortChange && sortField) {
      onSortChange(sortField, newDirection);
    }
    onClose();
  };
  
  return (
    <StyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width },
      }}
      {...props}
    >
      {showHeader && (
        <>
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <SortHeader>
              <SortIcon fontSize="small" sx={{ mr: 1 }} />
              Sort By
            </SortHeader>
          </Box>
          <Divider sx={{ opacity: 0.5 }} />
        </>
      )}
      
      {/* Sort options */}
      <Box sx={{ py: 1 }}>
        {sortOptions.map(option => (
          <StyledMenuItem
            key={option.field}
            onClick={() => handleSortClick(option.field)}
            selected={sortField === option.field}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {sortField === option.field && (
                <CheckIcon fontSize="small" color="primary" />
              )}
            </ListItemIcon>
            <ListItemText 
              primary={option.label}
              secondary={option.description}
            />
            {sortField === option.field && (
              <Box sx={{ ml: 'auto', pl: 2 }}>
                {sortDirection === 'asc' ? (
                  <AscendingIcon fontSize="small" color="action" />
                ) : (
                  <DescendingIcon fontSize="small" color="action" />
                )}
              </Box>
            )}
          </StyledMenuItem>
        ))}
      </Box>
      
      {/* Direction toggle */}
      {showDirection && sortField && (
        <>
          <Divider sx={{ opacity: 0.5 }} />
          <Box sx={{ py: 1 }}>
            <StyledMenuItem onClick={handleDirectionToggle}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {sortDirection === 'asc' ? (
                  <AscendingIcon fontSize="small" color="primary" />
                ) : (
                  <DescendingIcon fontSize="small" color="primary" />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                secondary="Click to toggle"
              />
            </StyledMenuItem>
          </Box>
        </>
      )}
    </StyledMenu>
  );
};

SortMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  
  // Sort state
  sortField: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  
  // Handlers
  onSortChange: PropTypes.func,
  onDirectionChange: PropTypes.func,
  
  // Options
  options: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
  })),
  defaultOptions: PropTypes.bool,
  
  // Display
  showDirection: PropTypes.bool,
  showHeader: PropTypes.bool,
  
  // Style
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default memo(SortMenu);